import { Component, HostListener, inject, NgZone, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';

import { BehaviorSubject, combineLatest, defer, EMPTY, forkJoin, MonoTypeOperatorFunction, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  pairwise,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  BusinessEntity,
  distinctUntilObjectChanged,
  getControlValueChanges,
  ignoreFalsy,
  ignoreNullish,
  LoadableStatus,
  Logger,
  SharedState,
} from '@aiwyn/shared';
import { AssetUriService, ConfigService, GetBusinessEntities, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { LegalService } from '../../services/legal.service';

import {
  AbstractPaymentMethod,
  CancelCurrentPayment,
  ClearCurrentPaymentActionRequirement,
  ClearQuickPaymentLinkId, GetPaymentFeeConfigs,
  GetQuickPaymentLinkId,
  MakePayment,
  PaymentActionRequirementDialogComponent,
  PaymentActionRequirementDialogData,
  PaymentCancellationSource,
  THREE_DS_USER_CANCEL_ERROR_NAME,
  PaymentFlow,
  PaymentMethodIntent,
  PaymentMethodMode,
  PaymentPlatformService,
  PaymentsSharedService,
  PaymentsState,
  PaymentSummaryComponent,
  PaymentTrackService,
  PaymentWorkflowEvent,
  PayOpenPortalPayable,
  RecipientEmailsComponent,
  RecipientEmailsDisabledState,
  SelectPaymentMethodComponent,
  SetAdditionalRecipientEmail,
  SetPayWithInvoices,
  SetProvidedPrimaryRecipientEmail,
  UpdatePayWithoutInvoicePayable,
} from '@aiwyn/common/payments';

import { AuthService, LoginAnonymouslyWithGhostClient } from '@aiwyn/auth';

import { BaseClient, ClientApiService, OpenPaymentLinkResult, StripeApiService } from '@aiwyn/api';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ROUTE_TOKEN_QUICK_PAYMENT_LINK_ERROR } from '../../routing-constants';

interface QuickPaymentLinkQueryParams {
  client?: string;
  companyName?: string;
  email?: string;
  amount?: string;
  paymentDescription?: string;
  qpLink?: string;
}

interface QuickPaymentFormValues {
  client: PaymentFormClient;
  companyName?: string | null;
  email?: string;
  amount?: string;
  paymentDescription?: string;
}

interface PaymentFormClient extends BaseClient {
  isGhostClient?: boolean;
}

@Component({
  selector: 'app-open-portal',
  templateUrl: './open-portal.component.html',
  styleUrls: ['./open-portal.component.scss'],
  standalone: false,
})
export class OpenPortalComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly paymentTrackService = inject(PaymentTrackService);
  private readonly zone = inject(NgZone);
  private readonly launchDarklyService = inject(LaunchDarklyService);

  readonly PaymentFlow = PaymentFlow;
  readonly PaymentMethodIntent = PaymentMethodIntent;

  // Email-first checkout kill switch + gate state (see PaymentsSidebarComponent for the mirror).
  isRequireEmailBeforePaymentEnabled$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_REQUIRE_EMAIL_ADDRESS_BEFORE_PAYMENT_METHOD);
  emailValid = signal(false);
  // Whether the payer's email was prepopulated on load — hides the gate status banners when true.
  emailGatePrepopulated = signal(false);
  private _methodsUnlockedTracked = false;

  // The URL's client param cannot change while this page lives, so it comes from the
  // route snapshot. The old shape piped queryParams through take(1) inside a combineLatest:
  // if that source did not replay for the async pipe's subscription, combineLatest never
  // emitted and the template was stuck on the company branch regardless of the server args.
  clientProvided$ = this.store.select(PaymentsState.getQuickPaymentLinkArgs).pipe(
    map(serverArgs => !!(this.activatedRoute.snapshot.queryParams as QuickPaymentLinkQueryParams).client || serverArgs?.clientId != null),
    distinctUntilChanged(),
  );
  loadedClients$ = new BehaviorSubject<BaseClient[]>([]);
  clientIdControl = new UntypedFormControl({ value: '', disabled: true });
  companyOrIndividualNameControl = new UntypedFormControl('');
  paymentDescriptionControl = new UntypedFormControl('', [Validators.required, Validators.maxLength(140)]);
  // Seeded null, not 0: a link without an amount must render an empty input, and the
  // deletion-only reset below no longer incidentally clears a numeric seed.
  amountControl = new UntypedFormControl(null, [Validators.required, Validators.min(0.01), Validators.maxLength(20)]);
  termsCheckboxControl = new UntypedFormControl(false, Validators.requiredTrue);

  get firmName(): string {
    return this.configService.customerName;
  }

  primaryEmail: string = '';
  recipientEmailDisabledState = signal<RecipientEmailsDisabledState>(false);

  paymentMethodMode$ = of(PaymentMethodMode.TEMPORARY);
  selectedPaymentMethod$ = this.paymentPlatformService.getSelectedPaymentMethod();
  selectedPaymentMethodId$ = this.selectedPaymentMethod$.pipe(
    map(paymentMethod => paymentMethod?.id),
    distinctUntilChanged(),
    delay(1), // ugly ExpressionChangedAfterItHasBeenCheckedError workaround
  );

  version$: Observable<string> = this.store.select(SharedState.getVersion);
  isInitializationCompleteSubject$ = new BehaviorSubject<boolean>(false);

  payWithoutInvoicePayable$ = this.store.select(PaymentsState.getPayWithoutInvoicePayable).pipe(
    delay(1), // ugly ExpressionChangedAfterItHasBeenCheckedError workaround
  );

  resetFormOnPayWithoutInvoicePayableDeletion$ = this.payWithoutInvoicePayable$.pipe(
    map(payable => !!payable),
    distinctUntilChanged(),
    pairwise(),
    // Only a deletion (payable existed, then vanished) resets the form. The select emits the
    // current state on subscribe, and during bootstrap the payable does not exist yet, so a
    // plain falsy check fires immediately and wipes the amount a qpLink's server-resolved
    // arguments just applied.
    filter(([hadPayable, hasPayable]) => hadPayable && !hasPayable),
    tap(_ => {
      // since typeId comes from a radio group and subtotal shouldn't ever be nullish, preserve some kind of value for them on reset
      this.amountControl.reset();
    }),
    takeUntil(this.destroy$),
  );

  isPaymentProcessing$ = new BehaviorSubject<boolean>(false);
  total$ = this.selectedPaymentMethod$.pipe(
    switchMap(selectedPaymentMethod => this.store.select(PaymentsState.getTotal(selectedPaymentMethod, { ignoreLateFees: true }))),
    delay(1), // ugly ExpressionChangedAfterItHasBeenCheckedError workaround
  );

  handlePaymentActionRequirements$ = this.store.select(PaymentsState.getCurrentPaymentActionRequirement).pipe(
    /**
     * Wait until a valid payment action requirement is emitted.
     */
    ignoreNullish(),
    filter(paymentActionRequirement => !paymentActionRequirement.cancelled),
    distinctUntilObjectChanged(),

    /**
     * Open a dialog to inform the user of the required action and prompt them to continue. Stripe handles the rest of the process.
     */
    switchMap(paymentActionRequirement => {
      // if other actions are required, paymentActionRequirement will be used to determine dialog data
      const dialog = this.dialog.open<PaymentActionRequirementDialogComponent, PaymentActionRequirementDialogData, boolean>(
        PaymentActionRequirementDialogComponent,
        { data: { header: 'This card requires 3D Secure Authentication' } },
      );

      return dialog.afterClosed().pipe(
        switchMap(shouldContinue => {
          if (!shouldContinue) {
            return of<Error>({ name: THREE_DS_USER_CANCEL_ERROR_NAME, message: 'The payment was canceled.' });
          }

          // user agreed to continue with 3D Secure Authentication
          return this.paymentPlatformService.getSelectedPaymentMethod().pipe(
            take(1),
            switchMap(selectedPaymentMethod =>
              this.paymentPlatformService.handlePaymentActionRequirement(paymentActionRequirement, selectedPaymentMethod.type),
            ),
          );
        }),
      );
    }),

    /**
     * React to user's decision. If the user cancels the required action, cancel the payment, which, in turn, cancels the entire batch.
     * Otherwise, clear the current payment action requirement.
     */
    switchMap(actionError => {
      if (actionError == undefined) {
        return this.store.dispatch(new ClearCurrentPaymentActionRequirement());
      }
      const cancellationSource: PaymentCancellationSource = actionError.name === THREE_DS_USER_CANCEL_ERROR_NAME
        ? PaymentCancellationSource.THREE_DS_USER_CANCEL
        : PaymentCancellationSource.THREE_DS_AUTH_FAILED;
      return this.store.dispatch(new CancelCurrentPayment(actionError, cancellationSource));
    }),
    takeUntil(this.destroy$),
  );

  constructor(
    private readonly paymentsSharedService: PaymentsSharedService,
    private readonly stripeApiService: StripeApiService,
    private readonly store: Store,
    private readonly logger: Logger,
    private readonly spinner: NgxSpinnerService,
    private readonly dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
    private paymentPlatformService: PaymentPlatformService,
    public assetsUriService: AssetUriService,
    public configService: ConfigService,
    public legalService: LegalService,
    private readonly logInWithGhostClient: LoginAnonymouslyWithGhostClient,
    private readonly router: Router,
    private readonly clientApiService: ClientApiService,
  ) { }

  ngOnInit(): void {
    this.spinner.show();
    this.store.dispatch(new SetPayWithInvoices(false));

    // Log in with a ghost client
    this.authService.logIn(this.logInWithGhostClient).pipe(
      // logIn can re-emit on auth-state churn, and a second emission cancels the in-flight
      // switchMap chain mid-bootstrap: the client fetch in applyServerResolvedArguments dies
      // and the link's arguments end up half-applied. Exactly one init chain may run.
      take(1),
      // logIn resolves in a raw Firebase SDK callback (onAuthStateChanged has no NgZone
      // re-entry), so without this everything below runs outside the Angular zone: the
      // controls get set but zone-driven bindings never repaint. Signal writes still
      // render, which is why only the email survived in the stalled state.
      this.continueInAngularZone(),
      switchMap(() => combineLatest([
        this.store.dispatch([
          new GetBusinessEntities(defer(() => this.stripeApiService.getBusinessEntities())),
          new GetPaymentFeeConfigs(),
        ]),
        this.activatedRoute.queryParams as Observable<QuickPaymentLinkQueryParams>
      ])),
      switchMap(([_, queryParams]) => {
        // If the client id is present in the URL, we want to use it in the form and state
        // Otherwise just grab the ghostClientID
        const clientIdRequest: Observable<BaseClient | number> = queryParams?.client
          ? this.clientApiService.getClientByKey(queryParams.client).pipe(
            tap((client) => this.loadedClients$.next([client as BaseClient]))
          )
          : this.authService.getUser().pipe(
            take(1),
            map(user => user.getClientIdList()[0]),
          );

        return forkJoin({
          clientValue: clientIdRequest,
          params: of(queryParams)
        });
      }),
      tap(({ clientValue, params}) => {
        const isGhostClient = (typeof clientValue === 'number');
        const client = isGhostClient ? { id: clientValue, name: '', isGhostClient } : { ...clientValue, isGhostClient };
        // If the client is not the ghost client, company name is ignored regardless of whether it exists (FIRM-6337)
        let companyName;
        if(isGhostClient) {
          companyName = params.companyName ? params.companyName : null;
        } else {
          companyName = null;
        }
        // Initialize the form fields with the correct values
        this.populateFormWithQueryValues({
          client,
          companyName,
          paymentDescription: params.paymentDescription || null,
          amount: params.amount || null,
          email: params.email || null
        });
      }),
      switchMap(_ => this.getQuickPaymentLinkIdIfPresent()), // TODO: Out of scope for change but this should probably be higher in the pipe considering the redirect
      switchMap(_ => this.applyServerResolvedArguments()),
      // Funnel entry for the quick-payment-link / open-portal flow. Fires after GetQuickPaymentLinkId
      // resolves so `surface` resolves to 'quick-payment-link' when a qpLink was supplied (rather than
      // the 'invoices' fallback that would land if we emitted at the top of ngOnInit).
      tap(_ => this.paymentTrackService.trackWorkflow(
        PaymentWorkflowEvent.PAYMENT_SETUP_STARTED,
        PaymentsState.buildPaymentEventBase(this.store),
      )),
      catchError(error => {
        this.logger.warn('AUTH : OpenPortalComponent : ngOnInit : Failed to log in to ghost client, how did I even get here.', error);
        this.spinner.hide()
        // Show generic error message
        return this.authService.showAuthFailureDialog(true);
      }),
      take(1),
      // A destroyed instance must not keep bootstrapping: without this, its orphan pipe
      // keeps logging in, applying arguments to dead controls, and racing the auth lock
      // against the live instance that actually renders.
      takeUntil(this.destroy$),
      finalize(() => {
        this.spinner.hide();
      })
    ).subscribe({
      // The form streams start only after every server-resolved argument is applied, so the
      // payable sync cannot publish half-populated values mid-bootstrap.
      complete: () => this.startFormStreams(),
    });

    this.handlePaymentActionRequirements$.subscribe();
  }

  private continueInAngularZone<T>(): MonoTypeOperatorFunction<T> {
    return source => new Observable<T>(subscriber => source.subscribe({
      next: value => this.zone.run(() => subscriber.next(value)),
      error: error => this.zone.run(() => subscriber.error(error)),
      complete: () => this.zone.run(() => subscriber.complete()),
    }));
  }

  private startFormStreams(): void {
    // The init pipe has no takeUntil, so its completion can outlive the component; a late
    // subscription here would miss the destroy notification and leak.
    if (this.destroy$.isStopped) {
      return;
    }

    this.resetFormOnPayWithoutInvoicePayableDeletion$.subscribe();

    combineLatest([
      getControlValueChanges(this.companyOrIndividualNameControl, true),
      getControlValueChanges(this.amountControl, true),
      getControlValueChanges(this.paymentDescriptionControl, true),
      getControlValueChanges(this.clientIdControl, true),
    ]).pipe(
      debounceTime(700),
      tap(([companyName, amount, paymentDescription, client]) => {

        const payOpenPortalPayable: Partial<PayOpenPortalPayable> = {
          companyName: companyName as string,
          subtotal: amount as number,
          notes: `${companyName} - ${paymentDescription}`,
          clientId: (client as BaseClient).id,
        };

        this.store.dispatch(new UpdatePayWithoutInvoicePayable(payOpenPortalPayable));
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  loadBusinessEntities(): Observable<BusinessEntity[]> {
    return this.stripeApiService.getBusinessEntities().pipe(
      tap(businessEntities => this.configService.businessEntityList$.next(businessEntities)),
    );
  }

  populateFormWithQueryValues(params: QuickPaymentFormValues): void {
    // Business case:
    // If the client is provided in URL we show the client autocomplete and hide the company name field
    // If the client is not provided, we show the company name field and hide the client autocomplete
    // IF both are provided, client name is ignored entirely

    // Client ID will be the url param or the ghost client id
    // This field is set regardless however it will be hidden if no client Id is provided in the URL
    this.clientIdControl.setValue(params.client);
    this.clientIdControl.setValidators([Validators.required]);
    this.clientIdControl.disable();
    this.clientIdControl.updateValueAndValidity();

    // If no client is provided in the URL, the company name field should be shown and prefilled if applicable
    if(params.client.isGhostClient) {
      if (params.companyName) {
        this.companyOrIndividualNameControl.setValue(params.companyName);
        this.companyOrIndividualNameControl.disable();
      }
      this.companyOrIndividualNameControl.setValidators([Validators.required]);
      this.companyOrIndividualNameControl.updateValueAndValidity();
    }

    if (params.email) {
      this.primaryEmail = params.email;
      this.recipientEmailDisabledState.set('primary');
    }

    if (params.amount) {
      const amount = Number(params.amount);
      this.amountControl.setValue(amount);
      this.amountControl.disable();
    }

    if (params.paymentDescription) {
      this.paymentDescriptionControl.setValue(params.paymentDescription);
      this.paymentDescriptionControl.disable();
    }
  }

  // The link's server-embedded arguments override whatever the URL carried: the URL is
  // tamperable by the payer, the metadata is not. A link without embedded arguments (or a
  // backend that predates them) leaves the URL-driven prefill untouched.
  applyServerResolvedArguments(): Observable<unknown> {
    const args: OpenPaymentLinkResult | undefined = this.store.selectSnapshot(PaymentsState.getQuickPaymentLinkArgs);
    if (!args) {
      return of(undefined);
    }
    if (args.usable === false) {
      // EMPTY halts the ngOnInit pipe so no payment-setup tracking fires for a dead link
      return defer(() => this.router.navigate([ROUTE_TOKEN_QUICK_PAYMENT_LINK_ERROR], { relativeTo: this.activatedRoute })).pipe(
        switchMap(() => EMPTY),
      );
    }
    if (args.amount != null) {
      this.amountControl.setValue(args.amount);
      this.amountControl.disable();
    }
    if (args.note) {
      this.paymentDescriptionControl.setValue(args.note);
      this.paymentDescriptionControl.disable();
    }
    if (args.email) {
      this.primaryEmail = args.email;
      this.recipientEmailDisabledState.set('primary');
    }
    if (args.clientId == null) {
      // Only the ghost-client path shows the company field; a provided client hides it,
      // so the server-embedded company name matters only when no client resolved.
      if (args.companyName) {
        this.companyOrIndividualNameControl.setValue(args.companyName);
        this.companyOrIndividualNameControl.disable();
      }
      return of(undefined);
    }
    return this.clientApiService.getClientById(args.clientId).pipe(
      tap((client: BaseClient | null) => {
        if (client) {
          this.loadedClients$.next([client]);
          this.clientIdControl.setValue({ ...client, isGhostClient: false });
          this.clientIdControl.disable();
          // The ghost-client path made the company name required before the server client
          // resolved; the template hides the field once a client exists, but hiding does not
          // clear the validator, and an invisible required control would block Pay Now forever.
          this.companyOrIndividualNameControl.clearValidators();
          this.companyOrIndividualNameControl.disable();
          this.companyOrIndividualNameControl.updateValueAndValidity();
        }
      }),
    );
  }

  getQuickPaymentLinkIdIfPresent(): Observable<unknown> {
    return (this.activatedRoute.queryParams as Observable<QuickPaymentLinkQueryParams>).pipe(
      take(1),
      map(({ qpLink }) => qpLink),
      switchMap(quickPaymentLink => {
        if (!quickPaymentLink) {
          return of(undefined);
        }

        return this.store.dispatch(new GetQuickPaymentLinkId(quickPaymentLink)).pipe(
          catchError(error => {
            this.logger.error('PAYMENTS : OpenPortalComponent : Error getting payment link', error);
            return this.router.navigate([ROUTE_TOKEN_QUICK_PAYMENT_LINK_ERROR], { relativeTo: this.activatedRoute });
          }),
        );
      }),
    );
  }

  onPrimaryEmailChanged(primaryEmail: string): void {
    this.store.dispatch(new SetProvidedPrimaryRecipientEmail(primaryEmail));
  }

  onAdditionalEmailsChanged(additionalEmails: string[]): void {
    this.store.dispatch(new SetAdditionalRecipientEmail(additionalEmails));
  }

  // Drive the method gate from recipient-emails' validity and fire the unlock funnel event once.
  onEmailValidChanged(isValid: boolean): void {
    this.emailValid.set(isValid);

    if (isValid && !this._methodsUnlockedTracked) {
      this._methodsUnlockedTracked = true;
      this.paymentTrackService.trackWorkflow(
        PaymentWorkflowEvent.PAYMENT_METHODS_UNLOCKED,
        PaymentsState.buildPaymentEventBase(this.store),
      );
    }
  }

  // Hide the gate status banners for a prepopulated, untouched email.
  onPrimaryEmailPrepopulated(prepopulated: boolean): void {
    this.emailGatePrepopulated.set(prepopulated);
  }

  // Locked-method click before a valid email — surface the email error and focus the field.
  onLockedPaymentMethodClick(): void {
    this.recipientEmailsComponent?.primaryEmailControl.markAsTouched();
    this.recipientEmailsComponent?.focusPrimaryEmail();
  }

  onPayNow(): void {
    this.isPaymentProcessing$.next(true);

    /**
     * Validate the form and begin the payment process.
     */
    this.validate().pipe(
      take(1),
      tap(isValid => this.isPaymentProcessing$.next(isValid)),
      ignoreFalsy(),
      switchMap(() => this.store.dispatch(new MakePayment())),

      /**
       * React to payment result.
       */
      tap(_ => {
        // maintain form state on failure
        const paymentStatus = this.store.selectSnapshot(PaymentsState.getLastExecutedPaymentBatchStatus);
        if (paymentStatus !== LoadableStatus.LOADED) {
          return;
        }

        // clear and/or enable form inputs on success
        [this.amountControl, this.paymentDescriptionControl].forEach(control => {
          control.reset();
          control.enable();
        });
        this.termsCheckboxControl.reset();
        this.companyOrIndividualNameControl.enable();
        this.recipientEmailDisabledState.set(false);
      }),
      catchError(_ => of(undefined)),
      switchMap(_ => {
        this.isPaymentProcessing$.next(false);
        return this.paymentsSharedService.onPaymentCompletion(true);
      }),

      /**
       * Stop processing if any payment within the current batch was cancelled.
       */
      takeUntil(
        this.store.select(PaymentsState.isLastExecutedPaymentBatchCancelled).pipe(
          skip(1), // skip the initial value (emits before current payment batch begins)
          distinctUntilChanged(),
          ignoreFalsy(),
          tap(_ => this.isPaymentProcessing$.next(false)),
        ),
      ),
    ).subscribe();
  }

  validateOpenPortalForm(): Observable<boolean> {
    this.amountControl.markAsTouched();
    this.companyOrIndividualNameControl.markAsTouched();
    this.paymentDescriptionControl.markAsTouched();
    this.clientIdControl.markAsTouched();

    return of(!this.amountControl.invalid && !this.companyOrIndividualNameControl.invalid && !this.paymentDescriptionControl.invalid && !this.clientIdControl.invalid);
  }

  @ViewChild(SelectPaymentMethodComponent) selectPaymentMethodComponent: SelectPaymentMethodComponent;
  @ViewChild(RecipientEmailsComponent) recipientEmailsComponent: RecipientEmailsComponent;
  @ViewChild(PaymentSummaryComponent) paymentSummaryComponent: PaymentSummaryComponent;

  validate(): Observable<boolean> {
    this.termsCheckboxControl.markAsTouched();

    return combineLatest([
      this.validateOpenPortalForm(),
      this.selectPaymentMethodComponent.validate(),
      this.recipientEmailsComponent.validate(),
      this.paymentSummaryComponent.validate(),
      of(this.termsCheckboxControl.valid),
    ]).pipe(
      take(1),
      map(validities => !validities.includes(false)),
    );
  }

  @HostListener('window:beforeunload')
  beforeUnload(): void {
    this.authService.signOut(false).pipe(
      take(1)
    ).subscribe();
  }

  ngOnDestroy(): void {
    const quickPaymentLinkIdStatus = this.store.selectSnapshot(PaymentsState.getQuickPaymentLinkIdStatus);
    // Snapshot before ClearQuickPaymentLinkId wipes quickPaymentLinkArgs.
    const isDeadLink = this.store.selectSnapshot(PaymentsState.getQuickPaymentLinkArgs)?.usable === false;
    if (quickPaymentLinkIdStatus !== LoadableStatus.DEFAULT) {
      this.store.dispatch(new ClearQuickPaymentLinkId());
    }

    // A failed or dead (usable:false) QPL navigates to the error page. Calling signout would effectively override that behavior and nav to login.
    if (quickPaymentLinkIdStatus !== LoadableStatus.ERROR && !isDeadLink) {
      this.authService.signOut(false).pipe(
        take(1)
      ).subscribe();
    }

    this.spinner.hide();

    this.destroy$.next();
    this.destroy$.complete();
  }

  onPaymentMethodModeChange(paymentMethodMode: PaymentMethodMode): void {
    this.paymentPlatformService.setPaymentMethodMode(paymentMethodMode).pipe(take(1)).subscribe();
  }

  onPaymentMethodSelected(paymentMethod: AbstractPaymentMethod): void {
    this.paymentPlatformService.setSelectedSavedPaymentMethodId(paymentMethod?.id).pipe(take(1)).subscribe();
  }

  clientAcDisplayFn = (client: BaseClient): string => client.name;
  compareClientFn = (client1: BaseClient, client2: BaseClient): boolean => client1.id === client2.id;
}
